import { cn } from "@/lib/utils";

export interface EmptyStateProps {
  title: string;
  description?: string;
  // Optional call to action below the description — e.g. "Create an agent" on a
  // page whose whole purpose is to make the first record.
  action?: React.ReactNode;
  // `compact` for an empty section inside a detail page, where the default
  // page-sized heading shouts over the section's own title. Page-level empties
  // keep the default.
  size?: "default" | "compact";
  className?: string;
}

// Centred title + description shown in place of a table when there is nothing
// to list.
//
// It replaces the table entirely rather than sitting under it: with no rows, the
// column headers and pagination bar describe nothing, so the host page hides
// them too.
//
// Two distinct cases, and the copy should say which: nothing exists yet, versus
// nothing matches the current filters. The second needs a way back — keep the
// action bar visible and offer to clear the filters.
export function EmptyState({
  title,
  description,
  action,
  size = "default",
  className,
}: EmptyStateProps) {
  const compact = size === "compact";

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center px-6 text-center",
        compact ? "py-8" : "h-full",
        className
      )}
    >
      <h3
        className={cn(
          "font-semibold tracking-tight",
          compact ? "text-base" : "text-2xl"
        )}
      >
        {title}
      </h3>
      {description ? (
        <p
          className={cn(
            "text-muted-foreground max-w-md text-sm",
            compact ? "mt-1" : "mt-2"
          )}
        >
          {description}
        </p>
      ) : null}
      {action ? (
        <div className={compact ? "mt-4" : "mt-6"}>{action}</div>
      ) : null}
    </div>
  );
}
